import { useEffect, useRef, useState } from 'react';
import {
  fetchAttachmentBlob,
  useConversation,
  useSyncConversation,
  type ConversationAttachment,
} from '../hooks/useHatMail';

interface ConversationDrawerProps {
  responseId: string;
  onClose: () => void;
}

function mountainTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Denver',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(date);
}

export default function ConversationDrawer({ responseId, onClose }: ConversationDrawerProps) {
  const syncStarted = useRef(false);
  const [originals, setOriginals] = useState<Set<string>>(new Set());
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const { data, isLoading, error } = useConversation(responseId);
  const sync = useSyncConversation();

  useEffect(() => {
    if (syncStarted.current) return;
    syncStarted.current = true;
    sync.mutate(responseId);
  }, [responseId]);

  const openAttachment = async (attachment: ConversationAttachment) => {
    setAttachmentError(null);
    try {
      const blob = await fetchAttachmentBlob(`/attachments/thread/${attachment.id}`);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.download = attachment.filename;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (openError) {
      setAttachmentError((openError as Error).message || 'Could not open attachment');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/35" role="dialog" aria-modal="true" aria-label="Email conversation">
      <button className="absolute inset-0 cursor-default" onClick={onClose} aria-label="Close conversation" />
      <aside className="relative flex h-full w-full flex-col bg-gray-50 shadow-2xl sm:max-w-xl">
        <header className="flex items-center justify-between border-b border-gray-200 bg-white px-4 py-4 sm:px-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-ghw-gold">Conversation</p>
            <h2 className="mt-0.5 text-lg font-semibold text-gray-900">
              {data ? `${data.message_count} message${data.message_count === 1 ? '' : 's'}` : 'Loading thread'}
            </h2>
          </div>
          <button onClick={onClose} className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50">
            Close
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
          {(isLoading || sync.isPending) && !data && (
            <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-500">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-200 border-t-ghw-gold" />
              Synchronizing the Gmail thread...
            </div>
          )}
          {(error || sync.isError) && !data && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              Conversation history could not be loaded. The reply card is still available.
            </div>
          )}
          {attachmentError && <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{attachmentError}</p>}
          <div className="space-y-3">
            {data?.messages.map((message) => {
              const showOriginal = originals.has(message.id);
              const text = showOriginal ? message.original_text : message.condensed_text;
              const isGhw = message.participant_type === 'ghw';
              return (
                <article
                  key={message.id}
                  className={`rounded-xl border p-4 ${isGhw ? 'border-ghw-gold/30 bg-ghw-cream/35' : 'border-gray-200 bg-white'}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-gray-900">
                        {message.sender_name || message.sender_email || 'Unknown sender'}
                      </p>
                      <p className="truncate text-xs text-gray-500">{message.sender_email}</p>
                    </div>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      isGhw ? 'bg-ghw-gold/15 text-ghw-brown' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {isGhw ? 'GHW' : message.participant_type === 'customer' ? 'Customer' : 'Other'}
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-gray-400">{mountainTime(message.sent_at_utc)}</p>
                  <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-gray-700">
                    {text || (message.condensation_status === 'purged' ? 'Message was deleted from Gmail and purged from HatMail.' : 'No text body available.')}
                  </p>
                  {message.original_text && message.condensed_text && message.original_text !== message.condensed_text && (
                    <button
                      type="button"
                      onClick={() => setOriginals((current) => {
                        const next = new Set(current);
                        if (next.has(message.id)) next.delete(message.id); else next.add(message.id);
                        return next;
                      })}
                      className="mt-3 text-xs font-semibold text-ghw-brown underline decoration-ghw-gold/50 underline-offset-2"
                    >
                      {showOriginal ? 'Show condensed message' : 'View complete original'}
                    </button>
                  )}
                  {message.attachments.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2 border-t border-gray-200/70 pt-3">
                      {message.attachments.map((attachment) => (
                        <button
                          key={attachment.id}
                          type="button"
                          onClick={() => openAttachment(attachment)}
                          className="rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:border-ghw-gold/50"
                        >
                          {attachment.filename}
                        </button>
                      ))}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      </aside>
    </div>
  );
}
